import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, Users, BookOpen, DollarSign } from 'lucide-react';
import api from '../../services/api';
import SimpleBarChart from '../../components/charts/SimpleBarChart';
import SimpleLineChart from '../../components/charts/SimpleLineChart';

interface AnalyticsData {
  overview: {
    totalUsers: number;
    totalCourses: number;
    totalEnrollments: number;
    totalRevenue: number;
    userGrowth: number;
    enrollmentGrowth: number;
  };
  usersByMonth: Array<{ label: string; value: number }>;
  enrollmentsByMonth: Array<{ label: string; value: number }>;
  topCourses: Array<{ title: string; enrollments: number }>;
  recentActivity: Array<{ type: string; description: string; time: string }>;
}

export default function Analytics() {
  const { t } = useTranslation('admin');
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<AnalyticsData | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/admin/analytics?period=${period}`);
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        // Set mock data for demo
        setData({
          overview: {
            totalUsers: 156,
            totalCourses: 12,
            totalEnrollments: 423,
            totalRevenue: 28500,
            userGrowth: 12.5,
            enrollmentGrowth: 8.3,
          },
          usersByMonth: [
            { label: 'Jan', value: 12 },
            { label: 'Feb', value: 18 },
            { label: 'Mar', value: 25 },
            { label: 'Apr', value: 22 },
            { label: 'May', value: 30 },
            { label: 'Jun', value: 35 },
          ],
          enrollmentsByMonth: [
            { label: 'Jan', value: 45 },
            { label: 'Feb', value: 52 },
            { label: 'Mar', value: 68 },
            { label: 'Apr', value: 75 },
            { label: 'May', value: 82 },
            { label: 'Jun', value: 95 },
          ],
          topCourses: [
            { title: 'Financial Analysis Fundamentals', enrollments: 89 },
            { title: 'Business Strategy Masterclass', enrollments: 67 },
            { title: 'Investment Management', enrollments: 54 },
            { title: 'Risk Management', enrollments: 42 },
            { title: 'Corporate Finance', enrollments: 38 },
          ],
          recentActivity: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [period]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  const periodLabels: Record<string, string> = {
    week: t('analytics.week'),
    month: t('analytics.month'),
    year: t('analytics.year'),
  };

  const overviewStats = [
    {
      title: t('analytics.totalUsers'),
      value: data?.overview.totalUsers || 0,
      change: data?.overview.userGrowth || 0,
      icon: Users,
      color: 'blue',
    },
    {
      title: t('analytics.totalCourses'),
      value: data?.overview.totalCourses || 0,
      change: 0,
      icon: BookOpen,
      color: 'green',
    },
    {
      title: t('analytics.totalEnrollments'),
      value: data?.overview.totalEnrollments || 0,
      change: data?.overview.enrollmentGrowth || 0,
      icon: TrendingUp,
      color: 'purple',
    },
    {
      title: t('analytics.totalRevenue'),
      value: `$${(data?.overview.totalRevenue || 0).toLocaleString()}`,
      change: 0,
      icon: DollarSign,
      color: 'gold',
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 mb-2">
            <BarChart3 className="w-6 h-6 text-gold" />
            <h1 className="font-display text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
          </div>
          <div className="flex gap-2">
            {(['week', 'month', 'year'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === p
                    ? 'bg-gold text-gray-900'
                    : 'bg-gray-100 text-gray-500 hover:text-gray-900'
                }`}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>
        </div>
        <p className="text-gray-500">{t('analytics.subtitle')}</p>
      </div>

      {/* Overview Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {overviewStats.map((stat) => (
          <div key={stat.title} className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-400`} />
              </div>
              {stat.change !== 0 && (
                <span className={`text-sm font-medium ${stat.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stat.change > 0 ? '+' : ''}{stat.change}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.title}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div className="glass-card rounded-2xl p-6">
          <SimpleLineChart
            data={data?.usersByMonth || []}
            title={t('analytics.userGrowth')}
            color="#3B82F6"
            height={250}
          />
        </div>
        <div className="glass-card rounded-2xl p-6">
          <SimpleBarChart
            data={data?.enrollmentsByMonth || []}
            title={t('analytics.monthlyEnrollments')}
            color="#D4AF37"
            height={250}
          />
        </div>
      </div>

      {/* Top Courses */}
      <div className="glass-card rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('analytics.topPerformingCourses')}</h3>
        <div className="space-y-4">
          {data?.topCourses.map((course, index) => (
            <div key={index} className="flex items-center gap-4">
              <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-900">
                {index + 1}
              </span>
              <div className="flex-1">
                <p className="text-gray-900 font-medium">{course.title}</p>
                <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gold rounded-full transition-all duration-300"
                    style={{
                      width: `${(course.enrollments / (data?.topCourses[0]?.enrollments || 1)) * 100}%`,
                    }}
                  />
                </div>
              </div>
              <span className="text-gray-500 text-sm">{t('analytics.enrollments', { count: course.enrollments })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
